import { useState } from "react";
import type {
  WeatherData,
  ScoreResult,
  Recommendation,
  SiteProfile,
  SchedulePhase,
} from "../types";
import { PHASES, COMPONENT_LABELS } from "../types";
import { colors, fonts } from "../tokens";
import { ScoreRing } from "./ScoreRing";
import { ScoreBar } from "./ScoreBar";

// ── Weather Card ──

export function WeatherCard({ weather, source }: { weather: WeatherData; source: string }) {
  const items = [
    { l: "TEMP", v: `${weather.temperature_f}°F` },
    { l: "WIND", v: `${weather.wind_speed_mph}mph` },
    { l: "PRECIP", v: `${weather.precipitation_probability}%` },
    { l: "VIS", v: `${weather.visibility_miles}mi` },
  ];
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 1, background: colors.border, borderRadius: 6, overflow: "hidden" }}>
        {items.map(({ l, v }) => (
          <div key={l} style={{ background: colors.surface, padding: "6px 4px", textAlign: "center" }}>
            <div style={{ fontSize: 7, color: colors.muted, fontFamily: fonts.mono, letterSpacing: "0.1em" }}>{l}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: colors.text, fontFamily: fonts.mono }}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 8, color: colors.muted, fontFamily: fonts.mono, textAlign: "center", marginTop: 4 }}>
        Weather: {source}
      </div>
    </div>
  );
}

// ── SIF Flags ──

export function SIFFlags({ flags }: { flags: string[] }) {
  if (flags.length === 0) return null;
  return (
    <div style={{ padding: "10px 12px", background: colors.redGlow, border: `1px solid ${colors.red}33`, borderRadius: 6 }}>
      <div style={{ fontSize: 9, fontFamily: fonts.mono, color: colors.red, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 5, fontWeight: 700 }}>
        ⚠ SIF-Potential ({flags.length})
      </div>
      {flags.map((f, i) => (
        <div key={i} style={{ fontSize: 11, color: colors.text2, marginBottom: 2, paddingLeft: 7, borderLeft: `2px solid ${colors.red}44` }}>{f}</div>
      ))}
    </div>
  );
}

// ── Recommendation Card ──

export function RecCard({ r }: { r: Recommendation }) {
  const col = r.priority === 1 ? colors.red : r.priority === 2 ? colors.amber : colors.blue;
  return (
    <div style={{ padding: "9px 11px", background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 5, borderLeft: `3px solid ${col}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
        <span style={{ fontSize: 9, fontWeight: 700, color: col, fontFamily: fonts.mono, textTransform: "uppercase", letterSpacing: "0.06em" }}>{r.category}</span>
        <span style={{ fontSize: 8, color: colors.muted, fontFamily: fonts.mono }}>P{r.priority}</span>
      </div>
      <p style={{ fontSize: 11, color: colors.text2, margin: "0 0 3px", lineHeight: 1.35 }}>{r.message}</p>
      <p style={{ fontSize: 11, color: colors.text, margin: 0, lineHeight: 1.35 }}>→ {r.action}</p>
    </div>
  );
}

// ── Recommendations Panel (SIF + Recs + empty state) ──

export function RecsPanel({ sif, recs, title }: { sif: string[]; recs: Recommendation[]; title?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <SIFFlags flags={sif} />
      {recs.length > 0 && (
        <div>
          <div style={{ fontFamily: fonts.mono, fontSize: 8, letterSpacing: "0.12em", color: colors.muted, textTransform: "uppercase", marginBottom: 6 }}>
            {title ?? "Recommendations"} ({recs.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {recs.map((r, i) => (
              <RecCard key={i} r={r} />
            ))}
          </div>
        </div>
      )}
      {recs.length === 0 && sif.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", color: colors.muted, background: colors.surface, borderRadius: 8, border: `1px solid ${colors.border}` }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>✓</div>
          <div style={{ fontSize: 13 }}>No actionable recommendations</div>
          <div style={{ fontSize: 11, marginTop: 4 }}>Low-risk conditions. Standard monitoring.</div>
        </div>
      )}
    </div>
  );
}

// ── Score Card (ring + bars + weather) ──

interface ScoreCardProps {
  result: ScoreResult;
  weather: WeatherData;
  wxSource: string;
  locLabel: string;
  dateStr: string;
  delta?: number | null;
}

export function ScoreCard({ result, weather, wxSource, locLabel, dateStr, delta }: ScoreCardProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${colors.border}` }}>
          <span style={{ fontFamily: fonts.mono, fontSize: 8, letterSpacing: "0.14em", color: colors.muted, textTransform: "uppercase" }}>
            Daily Risk Score
          </span>
          <span style={{ fontFamily: fonts.mono, fontSize: 8, color: colors.muted }}>{dateStr}</span>
        </div>
        <div style={{ position: "relative" }}>
          <ScoreRing score={result.overall} level={result.level} />
          {delta != null && delta !== 0 && (
            <div style={{
              position: "absolute", top: 8, right: 8,
              fontFamily: fonts.mono, fontSize: 12, fontWeight: 700,
              color: delta > 0 ? colors.red : colors.green,
              opacity: 1, transition: "opacity 0.3s ease",
            }}>
              {delta > 0 ? `+${delta}` : delta}
            </div>
          )}
        </div>
        <div style={{ textAlign: "center", fontSize: 10, color: colors.muted, margin: "10px 0 14px" }}>{locLabel}</div>
        {Object.entries(result.scores)
          .sort(([, a], [, b]) => b - a)
          .map(([k, v]) => (
            <ScoreBar key={k} label={COMPONENT_LABELS[k] ?? k} score={v} />
          ))}
      </div>
      <WeatherCard weather={weather} source={wxSource} />
    </div>
  );
}

// ── Integration Tag ──

function Tag({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <span style={{ fontSize: 8, fontFamily: fonts.mono, padding: "1px 6px", borderRadius: 2,
      background: `${color}18`, color, border: `1px solid ${color}33`, letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

// ── Collapsible Section ──

function Section({ title, tag, defaultOpen, children }: { title: string; tag?: React.ReactNode; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: 6, overflow: "hidden", marginBottom: 8 }}>
      <button onClick={() => setOpen(!open)}
        style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "10px 12px", background: colors.surface, border: "none", cursor: "pointer", color: colors.text }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontFamily: fonts.mono, fontSize: 10, letterSpacing: "0.08em", color: colors.text2 }}>{title}</span>
          {tag}
        </div>
        <span style={{ fontFamily: fonts.mono, fontSize: 10, color: colors.muted }}>{open ? "▾" : "▸"}</span>
      </button>
      {open && <div style={{ padding: "10px 12px", background: colors.raised, borderTop: `1px solid ${colors.border}` }}>{children}</div>}
    </div>
  );
}

// ── Number Field ──

function NumField({ label, value, onChange, min, max, step, future, current }: {
  label: string; value: number; onChange: (v: number) => void;
  min?: number; max?: number; step?: number; future?: string; current?: string;
}) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 8 }}>
      <div style={{ flex: 1 }}>
        <span style={{ fontSize: 11, color: colors.text2 }}>{label}</span>
        <div style={{ display: "flex", gap: 4, marginTop: 2 }}>
          {current && <Tag color={colors.green}>{current}</Tag>}
          {future && <Tag color={colors.purple}>FUTURE: {future}</Tag>}
        </div>
      </div>
      <input type="number" value={value} onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        min={min} max={max} step={step}
        style={{ width: 70, padding: "4px 8px", background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 4,
          color: colors.text, fontFamily: fonts.mono, fontSize: 12, textAlign: "right", outline: "none" }} />
    </div>
  );
}

// ── Toggle Field ──

function ToggleField({ label, checked, onToggle, future, current }: {
  label: string; checked: boolean; onToggle: (v: boolean) => void; future?: string; current?: string;
}) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
      <div>
        <span style={{ fontSize: 11, color: colors.text2 }}>{label}</span>
        <div style={{ display: "flex", gap: 4, marginTop: 2 }}>
          {current && <Tag color={colors.green}>{current}</Tag>}
          {future && <Tag color={colors.purple}>FUTURE: {future}</Tag>}
        </div>
      </div>
      <button onClick={() => onToggle(!checked)}
        style={{ width: 36, height: 20, borderRadius: 10, border: `1px solid ${checked ? colors.green : colors.border}`,
          background: checked ? colors.greenGlow : colors.bg, cursor: "pointer", position: "relative", padding: 0 }}>
        <div style={{ width: 14, height: 14, borderRadius: 7, background: checked ? colors.green : colors.muted,
          position: "absolute", top: 2, left: checked ? 19 : 2, transition: "left 0.15s ease" }} />
      </button>
    </div>
  );
}

// ── Phase Selector ──

function PhaseSelector({ value, onChange }: { value: SchedulePhase[]; onChange: (v: SchedulePhase[]) => void }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: colors.text2 }}>Active Phases</span>
        <div style={{ display: "flex", gap: 4 }}>
          <Tag color={colors.purple}>FUTURE: Procore</Tag>
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {PHASES.map((p) => {
          const on = value.includes(p.value);
          return (
            <button key={p.value} onClick={() => {
              const next = on ? value.filter((x) => x !== p.value) : [...value, p.value];
              onChange(next.length ? next : ["mobilization"]);
            }} style={{ padding: "4px 8px", fontSize: 10, fontFamily: fonts.mono,
              background: on ? colors.amberGlow : colors.bg, border: `1px solid ${on ? colors.amber : colors.border}`,
              borderRadius: 3, color: on ? colors.amber : colors.muted, cursor: "pointer" }}>
              {p.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Profile Editor (GC full configuration) ──

interface EditorProps {
  profile: SiteProfile;
  onUpdate: (path: string, value: unknown) => void;
}

export function ProfileEditor({ profile, onUpdate }: EditorProps) {
  return (
    <div>
      <div style={{ fontFamily: fonts.mono, fontSize: 8, letterSpacing: "0.12em", color: colors.muted, textTransform: "uppercase", marginBottom: 8 }}>
        Project Configuration
      </div>

      <Section title="SCHEDULE" tag={<Tag color={colors.purple}>FUTURE: Procore</Tag>} defaultOpen>
        <PhaseSelector value={profile.schedule_phases} onChange={(v) => onUpdate("schedule_phases", v)} />
        <NumField label="Concurrent High-Risk Zones" value={profile.concurrent} min={0} max={5}
          onChange={(v) => onUpdate("concurrent", v)} future="Procore + Zone mapping" />
        <NumField label="Schedule Pressure" value={profile.pressure} min={0} max={1} step={0.05}
          onChange={(v) => onUpdate("pressure", v)} future="Procore % complete" />
      </Section>

      <Section title="WORKFORCE" tag={<Tag color={colors.purple}>FUTURE: Badge System</Tag>} defaultOpen>
        <NumField label="Total Workers" value={profile.workforce.total_workers} min={0}
          onChange={(v) => onUpdate("workforce.total_workers", v)} future="Badge/sign-in" current="manual" />
        <NumField label="New Workers (<90 days)" value={profile.workforce.new_workers_under_90_days} min={0}
          onChange={(v) => onUpdate("workforce.new_workers_under_90_days", v)} future="Badge + HR" />
        <NumField label="Trades on Site" value={profile.workforce.trades_on_site} min={1}
          onChange={(v) => onUpdate("workforce.trades_on_site", v)} future="Procore subs" />
        <NumField label="Overtime Hours (week)" value={profile.workforce.overtime_hours_this_week} min={0} step={0.5}
          onChange={(v) => onUpdate("workforce.overtime_hours_this_week", v)} future="Payroll" />
        <ToggleField label="Night Shift" checked={profile.workforce.night_shift}
          onToggle={(v) => onUpdate("workforce.night_shift", v)} current="manual" />
      </Section>

      <Section title="COMPLIANCE" tag={<Tag color={colors.purple}>FUTURE: HammerTech</Tag>} defaultOpen>
        <NumField label="Open Violations" value={profile.compliance.open_violations} min={0}
          onChange={(v) => onUpdate("compliance.open_violations", v)} future="HammerTech" />
        <NumField label="Violations (7 days)" value={profile.compliance.violations_last_7_days} min={0}
          onChange={(v) => onUpdate("compliance.violations_last_7_days", v)} future="HammerTech" />
        <NumField label="Violations (30 days)" value={profile.compliance.violations_last_30_days} min={0}
          onChange={(v) => onUpdate("compliance.violations_last_30_days", v)} future="HammerTech" />
        <NumField label="PPE Compliance %" value={profile.compliance.ppe_compliance_rate} min={0} max={100}
          onChange={(v) => onUpdate("compliance.ppe_compliance_rate", v)} future="Cardo CV" current="manual" />
        <NumField label="Days Since Incident" value={profile.compliance.days_since_last_incident} min={0}
          onChange={(v) => onUpdate("compliance.days_since_last_incident", v)} future="Procore Incidents" />
        <ToggleField label="Toolbox Talk Done" checked={profile.compliance.toolbox_talk_completed_today}
          onToggle={(v) => onUpdate("compliance.toolbox_talk_completed_today", v)} current="daily input" />
      </Section>

      <Section title="EQUIPMENT" tag={<Tag color={colors.purple}>FUTURE: Telematics</Tag>} defaultOpen>
        <NumField label="Active Cranes" value={profile.equipment.cranes_active} min={0}
          onChange={(v) => onUpdate("equipment.cranes_active", v)} future="Telematics" current="manual" />
        <NumField label="Heavy Equipment" value={profile.equipment.heavy_equipment_count} min={0}
          onChange={(v) => onUpdate("equipment.heavy_equipment_count", v)} future="Telematics" />
        <NumField label="Man Lifts" value={profile.equipment.man_lifts_active} min={0}
          onChange={(v) => onUpdate("equipment.man_lifts_active", v)} future="Telematics" />
        <NumField label="Concrete Pumps" value={profile.equipment.concrete_pumps_active} min={0}
          onChange={(v) => onUpdate("equipment.concrete_pumps_active", v)} future="Telematics" />
        <ToggleField label="Critical Lift Planned" checked={profile.equipment.critical_lifts_planned}
          onToggle={(v) => onUpdate("equipment.critical_lifts_planned", v)} current="manual" />
      </Section>
    </div>
  );
}

// ── Daily Input (Subcontractor daily check-in) ──

interface DailyInputProps {
  profile: SiteProfile;
  onUpdate: (path: string, value: unknown) => void;
}

export function DailyInput({ profile, onUpdate }: DailyInputProps) {
  const dateStr = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });

  return (
    <div>
      <div style={{
        fontFamily: fonts.mono, fontSize: 8, letterSpacing: "0.12em",
        color: colors.muted, textTransform: "uppercase", marginBottom: 4,
      }}>
        Daily Check-in
      </div>
      <div style={{ fontSize: 11, color: colors.text2, marginBottom: 12 }}>
        {dateStr} — Update today's site conditions
      </div>

      <Section title="YOUR CREW" defaultOpen>
        <ToggleField label="Toolbox Talk Done" checked={profile.compliance.toolbox_talk_completed_today}
          onToggle={(v) => onUpdate("compliance.toolbox_talk_completed_today", v)} current="daily input" />
        <NumField label="Total Workers on Site" value={profile.workforce.total_workers} min={0}
          onChange={(v) => onUpdate("workforce.total_workers", v)} current="manual" />
        <NumField label="New Workers (<90 days)" value={profile.workforce.new_workers_under_90_days} min={0}
          onChange={(v) => onUpdate("workforce.new_workers_under_90_days", v)} current="manual" />
        <NumField label="Trades on Site" value={profile.workforce.trades_on_site} min={1}
          onChange={(v) => onUpdate("workforce.trades_on_site", v)} current="manual" />
        <ToggleField label="Night Shift" checked={profile.workforce.night_shift}
          onToggle={(v) => onUpdate("workforce.night_shift", v)} current="manual" />
        <NumField label="Overtime Hours (week)" value={profile.workforce.overtime_hours_this_week} min={0} step={0.5}
          onChange={(v) => onUpdate("workforce.overtime_hours_this_week", v)} current="manual" />
      </Section>

      <Section title="SAFETY" defaultOpen>
        <NumField label="Open Violations" value={profile.compliance.open_violations} min={0}
          onChange={(v) => onUpdate("compliance.open_violations", v)} current="manual" />
        <NumField label="New Violations Today" value={profile.compliance.violations_last_7_days} min={0}
          onChange={(v) => onUpdate("compliance.violations_last_7_days", v)} current="manual" />
        <NumField label="PPE Compliance %" value={profile.compliance.ppe_compliance_rate} min={0} max={100}
          onChange={(v) => onUpdate("compliance.ppe_compliance_rate", v)} current="manual" />
      </Section>

      <Section title="EQUIPMENT" defaultOpen>
        <NumField label="Active Cranes" value={profile.equipment.cranes_active} min={0}
          onChange={(v) => onUpdate("equipment.cranes_active", v)} current="manual" />
        <NumField label="Heavy Equipment" value={profile.equipment.heavy_equipment_count} min={0}
          onChange={(v) => onUpdate("equipment.heavy_equipment_count", v)} current="manual" />
        <NumField label="Man Lifts" value={profile.equipment.man_lifts_active} min={0}
          onChange={(v) => onUpdate("equipment.man_lifts_active", v)} current="manual" />
        <NumField label="Concrete Pumps" value={profile.equipment.concrete_pumps_active} min={0}
          onChange={(v) => onUpdate("equipment.concrete_pumps_active", v)} current="manual" />
        <ToggleField label="Critical Lift Planned" checked={profile.equipment.critical_lifts_planned}
          onToggle={(v) => onUpdate("equipment.critical_lifts_planned", v)} current="manual" />
      </Section>
    </div>
  );
}
